import type { DocumentRef } from '../../lib/documentReader'
import type { ObjectKind } from '../recordEdits/contract'
import type { BookFieldDefinition } from '../rkdecks/bookReview'

export type RemovalStatus = 'pending' | 'approved' | 'rejected' | 'cancelled'
export interface RemovalActor {
  id: string
  name?: string | null
  role?: string | null
  depositary_id?: string
}
export interface RemovalRequest {
  request_id: string
  status: RemovalStatus
  target_type: ObjectKind
  target_id: string
  reason: string
  requested_fund_id: string
  affected_fund_ids: string[]
  requested_by: string
  requested_at: string
  requester: RemovalActor
  request_binding: string
  request_receipt_id: string
  review_url: string
  message: string
  decision?: {
    action: RemovalStatus
    actor?: RemovalActor | null
    decided_at: string
    reason?: string | null
    receipt_id: string
    deletion_event_id?: string | null
  } | null
}
export interface RemovalDocument {
  document_id: string
  title?: string | null
  page?: number | null
}
export interface RemovalReference {
  target_type: 'fund' | 'position'
  target_id: string
  target_label?: string | null
  field: 'ifm_entity_id' | 'pledged_to_entity_id'
  before: string
  fund_ids: string[]
}
export interface RemovalSnapshot {
  schema_version: number
  target_type: ObjectKind
  target_id: string
  target: Record<string, unknown>
  requested_fund_id: string
  affected_fund_ids: string[]
  documents: RemovalDocument[]
  related_references: RemovalReference[]
  blockers: string[]
}
export interface RemovalReview {
  request: RemovalRequest
  original: RemovalSnapshot
  current: RemovalSnapshot | null
  changed_since_request: boolean
  binding: string | null
  can_approve: boolean
  can_reject: boolean
  can_cancel: boolean
  blockers: string[]
}
export interface RemovedObject {
  id: string
  target_type: ObjectKind
  fund_id: string
  deleted_at: string
  dependents_removed: Record<string, unknown>
}
export type RemovalAction = 'approve' | 'reject' | 'cancel'
export interface RemovalDecision {
  idempotency_key: string
  binding?: string | null
  reason?: string | null
}
export function isRemovalRequest(value: unknown): value is RemovalRequest {
  return (
    !!value &&
    typeof value === 'object' &&
    'request_id' in value &&
    typeof value.request_id === 'string'
  )
}
export function removalStatus(status: RemovalStatus): string {
  return {
    pending: 'Pending manager approval',
    approved: 'Removal approved',
    rejected: 'Removal rejected',
    cancelled: 'Removal request cancelled',
  }[status]
}
export function targetName(snapshot: RemovalSnapshot, fallback?: string): string {
  return String(
    snapshot.target.canonical_name ?? snapshot.target.name ?? fallback ?? snapshot.target_type
  )
}
export function removalDocuments(review: RemovalReview): DocumentRef[] {
  const documents = new Map<string, DocumentRef>()
  for (const source of [...review.original.documents, ...(review.current?.documents ?? [])])
    documents.set(source.document_id, {
      document_id: source.document_id,
      display_title: source.title,
      page: source.page,
    })
  return [...documents.values()]
}
function canonical(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonical)
  if (value && typeof value === 'object')
    return Object.fromEntries(
      Object.entries(value)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, item]) => [key, canonical(item)])
    )
  return value
}
export function sameRemovalValue(left: unknown, right: unknown): boolean {
  return JSON.stringify(canonical(left)) === JSON.stringify(canonical(right))
}
export function removalFields(
  fields: readonly BookFieldDefinition[],
  original: RemovalSnapshot,
  current: RemovalSnapshot | null,
  full: boolean
): BookFieldDefinition[] {
  return fields
    .filter((field) => {
      if (!(field.key in original.target) && !(current && field.key in current.target)) return false
      const before = original.target[field.key]
      const now = current?.target[field.key]
      return full || before != null || now != null
    })
    .sort((left, right) => left.order - right.order)
}
